// Cashflow orchestrator — composes timing + GST + waterfall into a
// month-by-month projection over a rolling horizon.
//
// Inputs:
//   - BudgetData (annual figures from the existing budget tool)
//   - CashflowData (timing profiles, bank accounts, TDs, waterfall, settings)
//   - CpiDefaults (pulled from BudgetData.settings; passed in so the
//     existing projectLine() helper can compound y0 → y1 across the
//     horizon's year boundary)
//
// Output: CashflowProjection — months[] in chronological order plus
// alerts[] surfacing the things the user needs to act on.

import type { BudgetData, LineItem } from '../../types';
import type {
  CashflowAlert,
  CashflowBankAccount,
  CashflowData,
  CashflowMonth,
  CashflowProjection,
  CashflowTermDeposit,
  TimingProfile,
  XeroCashByMonthData,
  XeroCashByMonthRow,
  XeroMonthlyActuals,
} from '../../cashflowTypes';
import { type CpiDefaults, projectLine } from '../projection';
import { allocateAnnualToMonths, generateMonthSequence, parseYm, ymKey } from './timing';
import { computePeriodNetGst, generateGstPeriodsForWindow } from './gst';

// Budget amounts are GST-exclusive. Cash flows for GST-applicable lines
// are grossed up by 1.15; the 15% portion is the GST liability paid to IRD.
const NZ_GST_RATE = 0.15;
import { applyWaterfall } from './waterfall';

export type ComputeCashflowInput = {
  budget: BudgetData;
  cashflow: CashflowData;
  monthlyActuals?: XeroMonthlyActuals;
  /** When provided, amounts for budget lines whose GL code appears in the
   *  sheet are sourced from the sheet instead of timing-profile calculations.
   *  Sheet amounts for hasGst rows are GST-inclusive; the engine converts
   *  them to GST-exclusive so the existing GST grossing-up logic is unchanged. */
  sheetData?: XeroCashByMonthData;
};

/** Returns a 12-element [Jan..Dec] allocation for a specific calendar
 *  year, picking the right annual amount per the budget's projection
 *  cascade. The horizon may straddle baseYear and baseYear+1; later
 *  rollovers (Y2/Y3) aren't supported in v1 because the horizon is
 *  capped at 12 months. */
function allocateForCalendarYear(
  line: LineItem,
  year: number,
  baseYear: number,
  cpiDefaults: CpiDefaults,
  profile: TimingProfile | null,
): number[] {
  const projected = projectLine(line, cpiDefaults);
  let annual: number;
  if (year === baseYear) annual = projected.current;
  else if (year === baseYear + 1) annual = projected.y1;
  else if (year === baseYear + 2) annual = projected.y2;
  else if (year === baseYear + 3) annual = projected.y3;
  else annual = projected.y3; // beyond — clamp
  return allocateAnnualToMonths(annual, profile);
}

/** Derives a 12-element [Jan..Dec] weight vector for a GL code by summing
 *  its absolute historical monthly amounts (up to lastFinalisedYm) across all
 *  available years and normalising to 1.0. Returns null when the code has no
 *  usable data. GST-inclusive vs exclusive cancels out in ratios, so raw sheet
 *  amounts are used directly. */
function deriveHistoricalWeights(
  sheetData: XeroCashByMonthData,
  code: string,
  lastFinalisedYm: string,
): number[] | null {
  const row = sheetData.rows.find((r) => r.code === code);
  if (!row) return null;
  const sumByM = new Array(12).fill(0) as number[];
  for (let i = 0; i < sheetData.periods.length; i++) {
    const ym = sheetData.periods[i];
    if (!ym || ym > lastFinalisedYm) continue;
    const amount = row.amounts[i];
    if (amount === null || amount === undefined || amount === 0) continue;
    const [, mIdx] = parseYm(ym);
    sumByM[mIdx] = (sumByM[mIdx] ?? 0) + Math.abs(amount);
  }
  const total = sumByM.reduce((s, v) => s + v, 0);
  if (total === 0) return null;
  return sumByM.map((v) => v / total);
}

/** Build a per-line, per-ym allocation map: { lineId: { ym: amount } }.
 *  Priority order for each projected month:
 *  1. Exact sheet value for that month (matched GL code, non-null amount)
 *  2. Budget annual figure distributed by historical monthly weights (derived
 *     from XeroCashByMonth data) when the line has a matched GL code with history
 *  3. Budget annual figure distributed by explicit timing profile
 *  4. Flat 1/12 distribution (default) */
function allocateLines(
  lines: LineItem[],
  baseYear: number,
  cpiDefaults: CpiDefaults,
  profilesByLine: Map<string, TimingProfile>,
  windowYms: string[],
  sheetByCode?: Map<string, XeroCashByMonthRow>,
  sheetPeriods?: string[],
  historicalWeightsByLine?: Map<string, number[]>,
): Record<string, Record<string, number>> {
  const out: Record<string, Record<string, number>> = {};
  const yearCache: Record<string, Record<number, number[]>> = {};
  for (const line of lines) {
    if (line.deletedAt) continue;
    const profile = profilesByLine.get(line.id) ?? null;
    const sheetRow = line.code ? sheetByCode?.get(line.code) : undefined;

    // Build effective profile from the explicit profile + historical weights.
    //   - No profile: substitute historical weights as a synthetic 'historical'
    //     profile (engine treats it the same as profile.kind === 'historical').
    //   - profile.kind === 'historical': substitute historical weights into
    //     customMonths so profileWeights returns the seasonal pattern. Falls
    //     back to flat monthly when no historical data exists for the line.
    //   - Any other kind: use as-is.
    const historicalW = historicalWeightsByLine?.get(line.id);
    let effectiveProfile: TimingProfile | null;
    if (!profile) {
      effectiveProfile = historicalW
        ? {
            lineId: line.id,
            kind: 'historical',
            anchorMonths: [],
            customMonths: historicalW,
            gstApplicable: false,
            excluded: false,
            displayOrder: 0,
            amountOverrides: null,
          }
        : null;
    } else if (profile.kind === 'historical') {
      effectiveProfile = historicalW
        ? { ...profile, customMonths: historicalW }
        : { ...profile, kind: 'monthly', customMonths: null };
    } else {
      effectiveProfile = profile;
    }

    const lineOut: Record<string, number> = {};
    const lineYearCache: Record<number, number[]> = {};
    out[line.id] = lineOut;
    yearCache[line.id] = lineYearCache;
    for (const ym of windowYms) {
      // Sheet data takes precedence for months where it has a non-null value.
      if (sheetRow && sheetPeriods) {
        const sheetIdx = sheetPeriods.indexOf(ym);
        const raw = sheetIdx >= 0 ? sheetRow.amounts[sheetIdx] : null;
        if (raw !== null && raw !== undefined) {
          // Sheet amounts for hasGst rows are GST-inclusive; convert to
          // GST-exclusive so the engine's 1.15 gross-up remains correct.
          lineOut[ym] = sheetRow.hasGst ? raw / (1 + NZ_GST_RATE) : raw;
          continue;
        }
      }
      // Fallback: budget allocation with effective timing profile.
      const [y, mIdx] = parseYm(ym);
      // NZ financial year runs Oct–Sep. The budget Y0/Y1/Y2 labels map to
      // the calendar year the FY ENDS: Oct–Dec belong to the FY ending
      // *next* calendar year; Jan–Sep belong to the FY ending *this* year.
      const fyLabel = mIdx >= 9 ? y + 1 : y;
      let yearly = lineYearCache[fyLabel];
      if (!yearly) {
        yearly = allocateForCalendarYear(line, fyLabel, baseYear, cpiDefaults, effectiveProfile);
        lineYearCache[fyLabel] = yearly;
      }
      lineOut[ym] = yearly[mIdx] ?? 0;
      const override = profile?.amountOverrides?.[ym];
      if (override !== undefined) lineOut[ym] = override;
    }
  }
  return out;
}

/** Sum a per-line allocation map into a single per-ym totals map,
 *  optionally filtering lines (e.g. only GST-applicable ones). */
function sumByYm(
  alloc: Record<string, Record<string, number>>,
  windowYms: string[],
  includeLine: (lineId: string) => boolean = () => true,
): Record<string, number> {
  const out: Record<string, number> = {};
  windowYms.forEach((ym) => {
    out[ym] = 0;
  });
  Object.keys(alloc).forEach((lineId) => {
    if (!includeLine(lineId)) return;
    const perLine = alloc[lineId];
    if (!perLine) return;
    windowYms.forEach((ym) => {
      out[ym] = (out[ym] ?? 0) + (perLine[ym] ?? 0);
    });
  });
  return out;
}

/** Match a YYYY-MM month against an ISO date — returns true when the
 *  date falls within that calendar month. */
function dateInMonth(isoDate: string, ym: string): boolean {
  if (!isoDate) return false;
  const d = new Date(isoDate);
  if (!isFinite(d.getTime())) return false;
  const got = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  return got === ym;
}

/** Estimate maturity proceeds for a TD: principal + simple interest
 *  over the (matDate - startDate) window. Good enough for cashflow. */
function maturityProceeds(td: CashflowTermDeposit): number {
  const start = new Date(td.startDate);
  const mat = new Date(td.maturityDate);
  if (!isFinite(start.getTime()) || !isFinite(mat.getTime())) return td.principal;
  const days = Math.max(0, (mat.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  const interest = td.principal * (td.rateAnnual / 100) * (days / 365);
  return td.principal + interest;
}

export function computeCashflow(input: ComputeCashflowInput): CashflowProjection {
  const { budget, cashflow } = input;
  const cpiDefaults: CpiDefaults = {
    cpiY1: budget.settings.cpiY1Default,
    cpiY2: budget.settings.cpiY2Default,
    cpiY3: budget.settings.cpiY3Default,
  };

  // Horizon: starts the month AFTER lastFinalisedMonth.
  const [lfY, lfMIdx] = parseYm(cashflow.settings.lastFinalisedMonth);
  const horizonStartYm = ymKey(lfY, lfMIdx + 1);
  const horizonMonths = Math.max(1, Math.min(36, cashflow.settings.horizonMonths));
  const windowYms = generateMonthSequence(horizonStartYm, horizonMonths);

  const profilesByLine = new Map<string, TimingProfile>();
  cashflow.timingProfiles.forEach((p) => profilesByLine.set(p.lineId, p));

  // Fallback GST flag: prefer explicit timing profile; fall back to
  // hasGst derived from the GL Cache (via XeroCashByMonthRow.hasGst).
  const hasGstByLineId = new Map<string, boolean>();
  if (input.sheetData) {
    const hasGstByCode = new Map(input.sheetData.rows.map((r) => [r.code, r.hasGst]));
    for (const line of [...budget.income, ...budget.expenses]) {
      if (line.deletedAt || !line.code) continue;
      const hasGst = hasGstByCode.get(line.code);
      if (hasGst !== undefined) hasGstByLineId.set(line.id, hasGst);
    }
  }

  const sheetByCode = input.sheetData
    ? new Map(input.sheetData.rows.map((r) => [r.code, r]))
    : undefined;
  const sheetPeriods = input.sheetData?.periods;

  // Derive monthly distribution weights from historical sheet data for every
  // budget line that has a matching GL code. These weights are used when:
  //  - the line has no explicit timing profile (default behaviour), OR
  //  - the line's profile kind is 'historical' (the user-selected default).
  // Lines whose profile kind is monthly/quarterly/annual/custom ignore the
  // weights and use the explicit profile instead.
  const historicalWeightsByLine = new Map<string, number[]>();
  if (input.sheetData) {
    const lastFinalised = cashflow.settings.lastFinalisedMonth;
    for (const line of [...budget.income, ...budget.expenses]) {
      if (line.deletedAt || !line.code) continue;
      const w = deriveHistoricalWeights(input.sheetData, line.code, lastFinalised);
      if (w) historicalWeightsByLine.set(line.id, w);
    }
  }

  const incomeAlloc = allocateLines(
    budget.income,
    budget.settings.baseYear,
    cpiDefaults,
    profilesByLine,
    windowYms,
    sheetByCode,
    sheetPeriods,
    historicalWeightsByLine,
  );
  const expenseAlloc = allocateLines(
    budget.expenses,
    budget.settings.baseYear,
    cpiDefaults,
    profilesByLine,
    windowYms,
    sheetByCode,
    sheetPeriods,
    historicalWeightsByLine,
  );

  // GST-applicable subsets (used for net-GST computation).
  const isGstLine = (lineId: string): boolean => {
    const p = profilesByLine.get(lineId);
    if (p !== undefined) return p.gstApplicable;
    return hasGstByLineId.get(lineId) ?? false;
  };
  // These are GST-exclusive subtotals for GST-applicable lines only.
  const incomeGstExcl = sumByYm(incomeAlloc, windowYms, isGstLine);
  const expenseGstExcl = sumByYm(expenseAlloc, windowYms, isGstLine);

  // Augment with HISTORICAL sheet sums so the projected GST schedule
  // can compute periods that straddle the historical→projected boundary.
  // Without this, a period like (Mar-Apr) historical → pay May projected
  // gets dropped because incomeGstExcl/expenseGstExcl only know projected
  // months.
  if (input.sheetData) {
    const sheet = input.sheetData;
    const lastFinalised = cashflow.settings.lastFinalisedMonth;
    const codeToIncomeId = new Map<string, string>();
    const codeToExpenseId = new Map<string, string>();
    for (const line of budget.income) if (line.code) codeToIncomeId.set(line.code, line.id);
    for (const line of budget.expenses) if (line.code) codeToExpenseId.set(line.code, line.id);

    for (let pi = 0; pi < sheet.periods.length; pi++) {
      const ym = sheet.periods[pi];
      if (!ym || ym > lastFinalised) continue;
      if (incomeGstExcl[ym] !== undefined && expenseGstExcl[ym] !== undefined) continue;
      let inc = 0;
      let exp = 0;
      for (const row of sheet.rows) {
        const raw = row.amounts[pi] ?? 0;
        if (raw === 0 || !row.hasGst) continue;
        const gstExcl = raw / (1 + NZ_GST_RATE);
        const inBudgetIncome = codeToIncomeId.has(row.code);
        const inBudgetExpense = codeToExpenseId.has(row.code);
        const isIncome = inBudgetIncome ? true : inBudgetExpense ? false : row.isIncome;
        if (isIncome) inc += gstExcl;
        else exp += gstExcl;
      }
      if (incomeGstExcl[ym] === undefined) incomeGstExcl[ym] = inc;
      if (expenseGstExcl[ym] === undefined) expenseGstExcl[ym] = exp;
    }
  }

  // GST schedule: derive periods from CCA's registered filing schedule
  // (periods end Feb/Apr/Jun/Aug/Oct/Dec; payment falls the following month).
  const gstPayByYm: Record<string, { amount: number; periodEnd: string }> = {};
  for (const p of generateGstPeriodsForWindow(windowYms)) {
    const collectedFraction = (incomeGstExcl[p.startYm] ?? 0) + (incomeGstExcl[p.endYm] ?? 0);
    const paidFraction = (expenseGstExcl[p.startYm] ?? 0) + (expenseGstExcl[p.endYm] ?? 0);
    const net = computePeriodNetGst(
      p,
      { [p.startYm]: collectedFraction * NZ_GST_RATE, [p.endYm]: 0 },
      { [p.startYm]: paidFraction * NZ_GST_RATE, [p.endYm]: 0 },
    );
    gstPayByYm[p.payYm] = { amount: net, periodEnd: p.endYm };
  }

  // Bank account state. We track per-account balances as cash moves
  // (inflows added to operating; drawdowns from savings/TDs as needed).
  const accountsById = new Map<string, CashflowBankAccount>();
  cashflow.bankAccounts.forEach((a) => accountsById.set(a.id, a));

  const operatingIds = new Set(
    cashflow.bankAccounts.filter((a) => a.role === 'Operating').map((a) => a.id),
  );
  // Payroll accounts are ring-fenced — never drained by the waterfall.
  const payrollIds = new Set(
    cashflow.bankAccounts.filter((a) => a.role === 'Payroll').map((a) => a.id),
  );

  const balByAccount = new Map<string, number>();
  cashflow.bankAccounts.forEach((a) => balByAccount.set(a.id, a.currentBalance));

  // TD principals — break-eligible. Once a TD matures it leaves this
  // map (proceeds drop into its destination account; auto-rollover keeps
  // it in the map at fresh principal, but we don't model the rollover's
  // new maturity in v1).
  const tdsById = new Map<string, CashflowTermDeposit>();
  cashflow.termDeposits.forEach((t) => tdsById.set(t.id, t));
  const balByTd = new Map<string, number>();
  cashflow.termDeposits.forEach((t) => balByTd.set(t.id, t.principal));

  const months: CashflowMonth[] = [];
  const alerts: CashflowAlert[] = [];
  let firstNegativeFlagged = false;

  // Running operating total for projection. Sum over all `operating`-role
  // accounts; drawdowns from savings/TDs sweep INTO the operating bucket.
  const operatingOpening = (): number => {
    let s = 0;
    operatingIds.forEach((id) => {
      s += balByAccount.get(id) ?? 0;
    });
    return s;
  };

  for (const ym of windowYms) {
    const opening = operatingOpening();

    // Per-line month figures
    const incomeByLine: Record<string, number> = {};
    const expenseByLine: Record<string, number> = {};
    let monthIncome = 0;
    let monthExpense = 0;
    Object.keys(incomeAlloc).forEach((id) => {
      const v = incomeAlloc[id]?.[ym] ?? 0;
      if (v !== 0) incomeByLine[id] = v;
      // Excluded lines are kept in incomeByLine for display but not counted.
      // GST-applicable lines: cash received = exclusive amount × 1.15.
      if (!profilesByLine.get(id)?.excluded) {
        monthIncome += isGstLine(id) ? v * (1 + NZ_GST_RATE) : v;
      }
    });
    Object.keys(expenseAlloc).forEach((id) => {
      const v = expenseAlloc[id]?.[ym] ?? 0;
      if (v !== 0) expenseByLine[id] = v;
      // GST-applicable lines: cash paid = exclusive amount × 1.15.
      if (!profilesByLine.get(id)?.excluded) {
        monthExpense += isGstLine(id) ? v * (1 + NZ_GST_RATE) : v;
      }
    });

    // GST payment hitting cash this month (positive=pay, negative=refund)
    const gstHere = gstPayByYm[ym];
    const gstNet = gstHere ? gstHere.amount : 0;
    if (gstHere && Math.abs(gstHere.amount) > 0.5) {
      alerts.push({
        kind: 'gst-due',
        ym,
        amount: gstHere.amount,
        periodEnd: gstHere.periodEnd,
      });
    }

    // TD maturities this month (only `to-account` policy moves cash).
    let tdMaturityIn = 0;
    cashflow.termDeposits.forEach((td) => {
      if (td.rolloverPolicy !== 'to-account') return;
      if (!dateInMonth(td.maturityDate, ym)) return;
      const proceeds = maturityProceeds(td);
      tdMaturityIn += proceeds;
      const dest = td.destinationAccountId;
      if (dest) {
        balByAccount.set(dest, (balByAccount.get(dest) ?? 0) + proceeds);
      }
      // Mark the TD as fully drained — no further break possible.
      balByTd.set(td.id, 0);
    });

    // Apply income/expense/GST to operating accounts. We attribute net
    // operating delta to the FIRST operating account by displayOrder
    // (since all operating accounts are usually one account anyway,
    // and tracking per-account splits would need per-line account
    // tagging — out of scope for v1).
    const sortedOperating = cashflow.bankAccounts
      .filter((a) => a.role === 'Operating')
      .sort((a, b) => a.displayOrder - b.displayOrder);
    const primaryOperating = sortedOperating[0];
    const netOperating = monthIncome - monthExpense - gstNet;
    if (primaryOperating) {
      balByAccount.set(
        primaryOperating.id,
        (balByAccount.get(primaryOperating.id) ?? 0) + netOperating,
      );
    }

    // Closing operating BEFORE drawdown
    let closingOperating = operatingOpening();

    // If primary operating goes negative, walk the waterfall.
    let drawdowns: CashflowMonth['drawdowns'] = [];
    if (primaryOperating && (balByAccount.get(primaryOperating.id) ?? 0) < 0) {
      const deficit = -(balByAccount.get(primaryOperating.id) ?? 0);
      // Defensive ring-fence: skip waterfall steps that target Payroll accounts.
      const ringfencedWaterfall = cashflow.waterfall.filter(
        (s) => !(s.sourceKind === 'account' && payrollIds.has(s.sourceId)),
      );
      const result = applyWaterfall(
        deficit,
        ym,
        ringfencedWaterfall,
        accountsById,
        tdsById,
        balByAccount,
        balByTd,
      );
      // Push the drawdown into the operating account.
      const covered = result.steps.reduce((s, st) => s + st.amount, 0);
      balByAccount.set(primaryOperating.id, (balByAccount.get(primaryOperating.id) ?? 0) + covered);
      drawdowns = result.steps.map((s) => ({
        sourceId: s.sourceId,
        sourceKind: s.sourceKind,
        amount: s.amount,
      }));
      // TDs broken early get their own alert.
      result.steps
        .filter((s) => s.sourceKind === 'term-deposit')
        .forEach((s) => {
          const td = tdsById.get(s.sourceId);
          if (!td) return;
          alerts.push({
            kind: 'td-broken-early',
            ym,
            tdId: td.id,
            tdReference: td.reference,
            lostInterest: s.brokenEarlyLostInterest ?? 0,
          });
        });
      if (result.uncovered > 0) {
        alerts.push({ kind: 'no-coverage', ym, deficit: result.uncovered });
      }
      closingOperating = operatingOpening();
    }

    if (!firstNegativeFlagged && closingOperating < 0) {
      alerts.push({ kind: 'first-negative', ym, deficit: -closingOperating });
      firstNegativeFlagged = true;
    }

    // Total liquid = sum of all accounts + remaining TD principals.
    let totalLiquid = 0;
    balByAccount.forEach((v) => {
      totalLiquid += v;
    });
    balByTd.forEach((v) => {
      totalLiquid += v;
    });

    months.push({
      ym,
      status: 'projected',
      openingOperating: opening,
      incomeByLine,
      expenseByLine,
      gstNet,
      tdMaturityIn,
      drawdowns,
      closingOperating,
      closingTotalLiquid: totalLiquid,
    });
  }

  // Prepend historical months from XeroCashByMonth sheet data (preferred source).
  // Falls back to monthlyActuals (Xero P&L tab) only when sheetData has no
  // historical periods. Sheet amounts for hasGst rows are GST-inclusive;
  // convert to GST-exclusive so the ×1.15 gross-up logic is unchanged.
  // Running balances derived backward from the projection's opening balance.
  if (input.sheetData && input.sheetData.periods.length > 0) {
    const sheet = input.sheetData;
    const historicalPeriods = sheet.periods.filter(
      (p) => p <= cashflow.settings.lastFinalisedMonth,
    );
    if (historicalPeriods.length > 0) {
      const codeToIncomeId = new Map<string, string>();
      const codeToExpenseId = new Map<string, string>();
      for (const line of budget.income) {
        if (line.code) codeToIncomeId.set(line.code, line.id);
      }
      for (const line of budget.expenses) {
        if (line.code) codeToExpenseId.set(line.code, line.id);
      }

      // Forward pass: build per-month income/expense figures and GST-exclusive
      // subtotals needed to compute the periodic IRD payment.
      type MonthData = {
        ym: string;
        incomeByLine: Record<string, number>;
        expenseByLine: Record<string, number>;
        totalIncome: number;
        totalExpense: number;
        incGstExcl: number;
        expGstExcl: number;
      };
      const perMonth: MonthData[] = [];
      for (const ym of historicalPeriods) {
        const periodIdx = sheet.periods.indexOf(ym);
        const incomeByLine: Record<string, number> = {};
        const expenseByLine: Record<string, number> = {};
        let totalIncome = 0;
        let totalExpense = 0;
        let incGstExcl = 0;
        let expGstExcl = 0;

        for (const row of sheet.rows) {
          const raw = periodIdx >= 0 ? (row.amounts[periodIdx] ?? 0) : 0;
          if (raw === 0) continue;
          const gstExcl = row.hasGst ? raw / (1 + NZ_GST_RATE) : raw;
          const inBudgetIncome = codeToIncomeId.has(row.code);
          const inBudgetExpense = codeToExpenseId.has(row.code);
          const isIncome = inBudgetIncome ? true : inBudgetExpense ? false : row.isIncome;
          if (isIncome) {
            const id = codeToIncomeId.get(row.code) ?? `_xero_${row.code}`;
            incomeByLine[id] = (incomeByLine[id] ?? 0) + gstExcl;
            const gstApplicable = profilesByLine.get(id)?.gstApplicable ?? row.hasGst;
            totalIncome += gstApplicable ? gstExcl * (1 + NZ_GST_RATE) : gstExcl;
            if (row.hasGst) incGstExcl += gstExcl;
          } else {
            const id = codeToExpenseId.get(row.code) ?? `_xero_${row.code}`;
            expenseByLine[id] = (expenseByLine[id] ?? 0) + gstExcl;
            const gstApplicable = profilesByLine.get(id)?.gstApplicable ?? row.hasGst;
            totalExpense += gstApplicable ? gstExcl * (1 + NZ_GST_RATE) : gstExcl;
            if (row.hasGst) expGstExcl += gstExcl;
          }
        }
        perMonth.push({
          ym,
          incomeByLine,
          expenseByLine,
          totalIncome,
          totalExpense,
          incGstExcl,
          expGstExcl,
        });
      }

      // Build the IRD GST payment schedule across the historical window.
      const gstExclByYm = new Map(
        perMonth.map((m) => [m.ym, { inc: m.incGstExcl, exp: m.expGstExcl }]),
      );
      const histGstPayByYm: Record<string, { amount: number; periodEnd: string }> = {};
      for (const p of generateGstPeriodsForWindow(historicalPeriods)) {
        const s = gstExclByYm.get(p.startYm) ?? { inc: 0, exp: 0 };
        const e = gstExclByYm.get(p.endYm) ?? { inc: 0, exp: 0 };
        const net = (s.inc + e.inc - (s.exp + e.exp)) * NZ_GST_RATE;
        histGstPayByYm[p.payYm] = { amount: net, periodEnd: p.endYm };
      }

      // Backward walk to derive opening balances from the known projection opening.
      const projectionOpening = months[0]?.openingOperating ?? 0;
      let runningBalance = projectionOpening;
      const actualMonths: CashflowMonth[] = [];

      for (let i = perMonth.length - 1; i >= 0; i--) {
        const pm = perMonth[i];
        if (!pm) continue;
        const { ym, incomeByLine, expenseByLine, totalIncome, totalExpense } = pm;
        const gstNet = histGstPayByYm[ym]?.amount ?? 0;
        const net = totalIncome - totalExpense - gstNet;
        const closing = runningBalance;
        const opening = closing - net;
        runningBalance = opening;

        actualMonths.unshift({
          ym,
          status: 'actual',
          openingOperating: opening,
          incomeByLine,
          expenseByLine,
          gstNet,
          tdMaturityIn: 0,
          drawdowns: [],
          closingOperating: closing,
          closingTotalLiquid: closing,
        });
      }

      return { months: [...actualMonths, ...months], alerts };
    }
  }

  // Fallback: prepend actual months from the XeroActualsByMonth Sheet tab
  // when sheetData has no historical periods.
  if (input.monthlyActuals && input.monthlyActuals.periods.length > 0) {
    const actuals = input.monthlyActuals;
    const isIncomeSec = (s: string) => /income|revenue|trading/i.test(s);

    // Build code → lineId maps so actuals show against budget lines
    // and we can look up each line's gstApplicable flag.
    const codeToIncomeId = new Map<string, string>();
    const codeToExpenseId = new Map<string, string>();
    for (const line of budget.income) {
      if (line.code) codeToIncomeId.set(line.code, line.id);
    }
    for (const line of budget.expenses) {
      if (line.code) codeToExpenseId.set(line.code, line.id);
    }

    // Build per-month GST-exclusive subtotals for GST-applicable lines.
    const actualIncGstExcl: Record<string, number> = {};
    const actualExpGstExcl: Record<string, number> = {};
    for (const ym of actuals.periods) {
      actualIncGstExcl[ym] = 0;
      actualExpGstExcl[ym] = 0;
      for (const [code, byYm] of Object.entries(actuals.byCode)) {
        const v = byYm[ym] ?? 0;
        if (v === 0) continue;
        const sec = actuals.sectionByCode[code] ?? '';
        const id = isIncomeSec(sec) ? codeToIncomeId.get(code) : codeToExpenseId.get(code);
        if (id && profilesByLine.get(id)?.gstApplicable) {
          if (isIncomeSec(sec)) actualIncGstExcl[ym] += v;
          else actualExpGstExcl[ym] += v;
        }
      }
    }

    // Schedule GST IRD payments within the actual window using the fixed NZ calendar.
    const actualGstPayByYm: Record<string, { amount: number; periodEnd: string }> = {};
    for (const p of generateGstPeriodsForWindow(actuals.periods)) {
      const collected = (actualIncGstExcl[p.startYm] ?? 0) + (actualIncGstExcl[p.endYm] ?? 0);
      const paid = (actualExpGstExcl[p.startYm] ?? 0) + (actualExpGstExcl[p.endYm] ?? 0);
      actualGstPayByYm[p.payYm] = {
        amount: (collected - paid) * NZ_GST_RATE,
        periodEnd: p.endYm,
      };
    }

    // Build actual CashflowMonth rows, walking backwards to derive
    // opening balances from the known projection opening.
    const projectionOpening = months[0]?.openingOperating ?? 0;
    let runningBalance = projectionOpening;
    const actualMonths: CashflowMonth[] = [];

    for (let i = actuals.periods.length - 1; i >= 0; i--) {
      const ym = actuals.periods[i];
      if (!ym) continue;
      const incomeByLine: Record<string, number> = {};
      const expenseByLine: Record<string, number> = {};
      let totalIncome = 0;
      let totalExpense = 0;

      for (const [code, byYm] of Object.entries(actuals.byCode)) {
        const v = byYm[ym] ?? 0;
        if (v === 0) continue;
        const sec = actuals.sectionByCode[code] ?? '';
        if (isIncomeSec(sec)) {
          const id = codeToIncomeId.get(code) ?? `_xero_${code}`;
          incomeByLine[id] = (incomeByLine[id] ?? 0) + v;
          const gst = profilesByLine.get(id)?.gstApplicable ?? false;
          totalIncome += gst ? v * (1 + NZ_GST_RATE) : v;
        } else {
          const id = codeToExpenseId.get(code) ?? `_xero_${code}`;
          expenseByLine[id] = (expenseByLine[id] ?? 0) + v;
          const gst = profilesByLine.get(id)?.gstApplicable ?? false;
          totalExpense += gst ? v * (1 + NZ_GST_RATE) : v;
        }
      }

      const gstNet = actualGstPayByYm[ym]?.amount ?? 0;
      const net = totalIncome - totalExpense - gstNet;
      const closing = runningBalance;
      const opening = closing - net;
      runningBalance = opening;

      actualMonths.unshift({
        ym,
        status: 'actual',
        openingOperating: opening,
        incomeByLine,
        expenseByLine,
        gstNet,
        tdMaturityIn: 0,
        drawdowns: [],
        closingOperating: closing,
        closingTotalLiquid: closing,
      });
    }

    return { months: [...actualMonths, ...months], alerts };
  }

  return { months, alerts };
}
